import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  Analytics as AnalyticsIcon,
  RecordVoiceOver as TtsIcon,
  SmartToy as LlmIcon,
  Hearing as SttIcon,
  AddIcCall as CreateCallIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  ExitToApp as LogoutIcon,
  Tune as GeneralIcon,
  ExpandMore
} from '@mui/icons-material';
import { authService, assistantService } from '../utils/api';

const drawerWidth = 240;

function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentAssistant, setCurrentAssistant] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const assistant = assistantService.getSelectedAssistant();
    setCurrentAssistant(assistant);
    const role = authService.getUserRole();
    setUserRole(role || 'viewer');
    
    authService.getCurrentUser()
      .then(user => {
        if (user && user.email) {
          setUserEmail(user.email);
        }
      })
      .catch(err => {
        console.error('Failed to get user:', err);
        setUserEmail('user@example.com');
      });
  }, []);

  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => setDrawerOpen((v) => !v);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleSwitchAssistant = () => {
    navigate('/assistants');
  };

  const menuItems = React.useMemo(() => {
    const assistantId = currentAssistant?.id;
    if (!assistantId) return [];
    
    return [
      {
        text: 'General',
        icon: <GeneralIcon sx={{ fontSize: 20 }} />,
        path: `/assistant/${assistantId}/general`
      },
      {
        text: 'Users',
        icon: <PeopleIcon sx={{ fontSize: 20 }} />,
        path: `/assistant/${assistantId}/users`
      },
      {
        text: 'Prompts',
        icon: <EditIcon sx={{ fontSize: 20 }} />,
        path: `/assistant/${assistantId}/prompts`
      },
      {
        text: 'Analysis Prompt',
        icon: <AnalyticsIcon sx={{ fontSize: 20 }} />,
        path: `/assistant/${assistantId}/analysis-prompt`
      },
      {
        text: 'Text to Speech',
        icon: <TtsIcon sx={{ fontSize: 20 }} />,
        path: `/assistant/${assistantId}/tts`
      },
      {
        text: 'LLM',
        icon: <LlmIcon sx={{ fontSize: 20 }} />,
        path: `/assistant/${assistantId}/llm`
      },
      {
        text: 'Speech to Text',
        icon: <SttIcon sx={{ fontSize: 20 }} />,
        path: `/assistant/${assistantId}/stt`
      },
      {
        text: 'Create Call',
        icon: <CreateCallIcon sx={{ fontSize: 20 }} />,
        path: `/assistant/${assistantId}/create-call`
      }
    ];
  }, [currentAssistant]);

  const isActive = (path) => location.pathname === path;
  const activeTitle = React.useMemo(() => {
    const found = menuItems.find((i) => isActive(i.path));
    return found?.text || 'Dashboard';
  }, [menuItems, location.pathname]);

  const TellyMark = () => (
    <Box
      component="svg"
      viewBox="0 0 64 20"
      sx={{ width: 32, height: 12, display: 'block' }}
      aria-hidden="true"
    >
      <path
        d="M2 10c6-8 12-8 18 0s12 8 18 0 12-8 18 0"
        fill="none"
        stroke="#14b8a6"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.95"
      />
    </Box>
  );

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
        borderRight: '1px solid rgba(102, 117, 140, 0.12)'
      }}
    >
      {/* Logo header (layout only; no dashboard logic) */}
      <Box
        sx={{
          px: 2.25,
          py: 1.75,
          borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
          backgroundColor: '#ffffff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.05rem',
              letterSpacing: 0.2,
              color: '#2447cc',
              lineHeight: 1
            }}
          >
            telly
          </Typography>
          <TellyMark />
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive(item.path) ? 'rgba(46, 91, 255, 0.08)' : 'transparent',
                  border: isActive(item.path) ? '1px solid rgba(46, 91, 255, 0.22)' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive(item.path)
                      ? 'rgba(46, 91, 255, 0.12)'
                      : 'rgba(102, 117, 140, 0.04)',
                    transform: 'translateX(2px)',
                    boxShadow: isActive(item.path)
                      ? '0 4px 12px rgba(46, 91, 255, 0.15)'
                      : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  },
                  '&:active': { transform: 'translateX(1px)' }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 500,
                    fontSize: '0.875rem',
                    color: isActive(item.path) ? 'primary.main' : 'text.primary'
                  }}
                />
                {isActive(item.path) && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Logout footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(102, 117, 140, 0.08)',
          background: 'rgba(102, 117, 140, 0.02)'
        }}
      >
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(239, 71, 111, 0.08)',
                transform: 'translateX(2px)',
                boxShadow: '0 2px 8px rgba(239, 71, 111, 0.12)'
              },
              '&:active': { transform: 'translateX(1px)' }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.primary'
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={isMobile ? drawerOpen : false}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none'
            }
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer (toggleable) */}
        {!isMobile && drawerOpen && (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                border: 'none'
              }
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          width: { md: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%' }
        }}
      >
        {/* Top bar */}
        <AppBar
          position="sticky"
          color="transparent"
          elevation={0}
          sx={{
            borderBottom: '1px solid rgba(102, 117, 140, 0.12)',
            backgroundColor: 'background.paper',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          <Toolbar sx={{ py: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 2,
                '&:hover': { backgroundColor: 'rgba(46, 91, 255, 0.08)' }
              }}
            >
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary' }}
            >
              {activeTitle}
            </Typography>

            {currentAssistant && (
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                endIcon={<SwitchIcon />}
                onClick={handleSwitchAssistant}
                sx={{
                  mr: 2,
                  borderColor: 'rgba(46, 91, 255, 0.3)',
                  color: 'text.primary',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  maxWidth: 260,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(46, 91, 255, 0.05)',
                    color: 'primary.main'
                  }
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: '0.875rem' }}
                    noWrap
                  >
                    {currentAssistant.assistant_name || currentAssistant.name}
                  </Typography>
                </Box>
              </Button>
            )}

            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ '&:hover': { backgroundColor: 'rgba(46, 91, 255, 0.08)' } }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 700
                }}
              >
                {userEmail ? userEmail.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <ExpandMore sx={{ color: '#94a3b8', fontSize: 18, ml: 0.5 }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 0.5,
                  minWidth: 200,
                  borderRadius: 2,
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                  border: '1px solid rgba(102, 117, 140, 0.12)'
                }
              }}
            >
              <MenuItem disabled sx={{ opacity: 1, py: 1 }}>
                <ListItemText
                  primary={userEmail || 'User'}
                  secondary={userRole}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: '#64748b' }} />
                </ListItemIcon>
                <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}>
                  Logout
                </ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {children}
      </Box>
    </Box>
  );
}

export default DashboardLayout;
